import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import ReportPrintDashboard from './ReportPrintDashboard';
import ReportPrintCarDetails from './ReportPrintCarDetails';
import ReportPrintSigefDetails from './ReportPrintSigefDetails';
import ReportPrintSncrDetails from './ReportPrintSncrDetails';
import ReportPrintCafirDetails from './ReportPrintCafirDetails';
import ReportPrintCcirDetails from './ReportPrintCcirDetails';
import ReportPrintCndDetails from './ReportPrintCndDetails';
import ReportPrintEntidadesDetails from './ReportPrintEntidadesDetails';
import ReportPrintLegalCivilDetails from './ReportPrintLegalCivilDetails';
import ReportPrintLegalCriminalDetails from './ReportPrintLegalCriminalDetails';
import ReportPrintSobreposicaoDetails from './ReportPrintSobreposicaoDetails';
import ReportPrintGeoespacialDetails from './ReportPrintGeoespacialDetails';
import { useReportLayoutConfig } from '../../contexts/ReportLayoutContext.jsx';

function formatDate(value) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString('pt-BR');
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString('pt-BR');
}

function PrintPage({ title, snapshot, userName, children }) {
  const now = new Date();
  const consolidatedAt = snapshot?._metadata?.consolidated_at;

  return (
    <Box className="report-print-page">
      <Box
        sx={{
          minHeight: '260mm',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          maxWidth: 'var(--report-content-width)',
          mx: 'auto',
          px: 4,
          py: 3,
          boxSizing: 'border-box',
        }}
      >
        <Box component="header" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

        <Box sx={{ flexGrow: 1 }}>{children}</Box>

        <Box
          component="footer"
          sx={{
            mt: 2,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <Typography variant="caption">Data do relatório: {formatDate(now)}</Typography>
          <Typography variant="caption">
            Consolidado em: {formatDateTime(consolidatedAt)}
          </Typography>
          <Typography variant="caption">Usuário: {userName || '-'}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function ReportPrintLayout({
  snapshot,
  userName,
  docsFiles,
  onReadyForPrint,
  mapSnapshots,
  filterSectionKey,
}) {
  const [printRefreshToken, setPrintRefreshToken] = useState(0);
  const { getSectionLayout, getPageSettings, layout } = useReportLayoutConfig();

  useEffect(() => {
    if (typeof onReadyForPrint === 'function') {
      onReadyForPrint();
    }
  }, [onReadyForPrint]);

  useEffect(() => {
    const handleBeforePrint = () => {
      setPrintRefreshToken((token) => token + 1);
    };

    window.addEventListener('beforeprint', handleBeforePrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, []);

  if (!snapshot) {
    return null;
  }

  // Mapeamento estático entre sectionKey lógico (banco/layout) e a estrutura atual de páginas
  const baseSections = [
    { id: 'executive-dashboard', sectionKey: 'dashboard', title: 'Resumo Executivo (Dashboard)' },
    { id: 'details-car', sectionKey: 'car', title: 'Detalhes Cadastrais - CAR' },
    { id: 'details-sigef', sectionKey: 'sigef', title: 'Detalhes Cadastrais - SIGEF' },
    { id: 'details-sncr', sectionKey: 'sncr', title: 'Detalhes Cadastrais - SNCR' },
    { id: 'details-cafir', sectionKey: 'cafir', title: 'Detalhes Cadastrais - CAFIR' },
    { id: 'details-ccir', sectionKey: 'serpro_ccir', title: 'Detalhes Cadastrais - CCIR' },
    { id: 'details-cnd', sectionKey: 'serpro_cnd', title: 'Detalhes Cadastrais - CND' },
    { id: 'details-entidades', sectionKey: 'entidades', title: 'Detalhes Cadastrais - Entidades' },
    {
      id: 'details-legal-civil',
      sectionKey: 'legal_civil',
      title: 'Detalhes Cadastrais - Jurídico - Cível',
    },
    {
      id: 'details-legal-criminal',
      sectionKey: 'legal_criminal',
      title: 'Detalhes Cadastrais - Jurídico - Criminal',
    },
    { id: 'overlaps-biomas', sectionKey: 'sobreposicao_biomas', title: 'Sobreposições - Biomas' },
    {
      id: 'overlaps-ucs',
      sectionKey: 'sobreposicao_uc',
      title: 'Sobreposições - Unidades de Conservação',
    },
    {
      id: 'overlaps-florestas-publicas',
      sectionKey: 'sobreposicao_florestas_publicas',
      title: 'Sobreposições - Florestas Públicas',
    },
    {
      id: 'overlaps-terras-indigenas',
      sectionKey: 'sobreposicao_terras_indigenas',
      title: 'Sobreposições - Terras Indígenas',
    },
    {
      id: 'overlaps-aldeias',
      sectionKey: 'sobreposicao_aldeias',
      title: 'Sobreposições - Aldeias',
    },
    {
      id: 'overlaps-terras-quilombolas',
      sectionKey: 'sobreposicao_terras_quilombolas',
      title: 'Sobreposições - Terras Quilombolas',
    },
    {
      id: 'overlaps-assentamentos-incra',
      sectionKey: 'sobreposicao_assentamentos_incra',
      title: 'Sobreposições - Assentamentos INCRA',
    },
    {
      id: 'overlaps-mineracao',
      sectionKey: 'sobreposicao_mineracao',
      title: 'Sobreposições - Mineração',
    },
    {
      id: 'overlaps-embargos-ibama',
      sectionKey: 'sobreposicao_embargos_ibama',
      title: 'Sobreposições - Embargos do IBAMA',
    },
    {
      id: 'overlaps-pra',
      sectionKey: 'sobreposicao_pra',
      title: 'Sobreposições - Plano de Recuperação Ambiental (PRA)',
    },
    {
      id: 'geoespacial',
      sectionKey: 'geoespacial',
      title: 'Geoespacial - Inventário de Áreas Registradas',
    },
  ];

  // Atualizar variáveis CSS globais de página de acordo com o layout, quando disponível
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!getPageSettings || typeof document === 'undefined') return;
    const pageSettings = getPageSettings();
    if (!pageSettings || typeof pageSettings !== 'object') return;

    const rootStyle = document.documentElement.style;

    if (pageSettings.marginTop) {
      rootStyle.setProperty('--report-page-margin-top', pageSettings.marginTop);
    }
    if (pageSettings.marginBottom) {
      rootStyle.setProperty('--report-page-margin-bottom', pageSettings.marginBottom);
    }
    if (pageSettings.contentWidth) {
      rootStyle.setProperty('--report-content-width', pageSettings.contentWidth);
    }
    if (pageSettings.headerHeight) {
      rootStyle.setProperty('--report-header-height', pageSettings.headerHeight);
    }
    if (pageSettings.footerHeight) {
      rootStyle.setProperty('--report-footer-height', pageSettings.footerHeight);
    }
  }, [getPageSettings, layout]);

  // Enriquecer seções com informações de layout quando disponíveis
  let sections = baseSections
    .map((section, index) => {
      const layoutSection = getSectionLayout ? getSectionLayout(section.sectionKey) : null;
      return {
        ...section,
        layout: layoutSection,
        defaultOrder: index + 1,
      };
    })
    .filter((section) => {
      // Se o layout trouxer visible=false, esconder a página; caso contrário, manter comportamento atual
      if (section.layout && typeof section.layout.visible === 'boolean') {
        return section.layout.visible;
      }
      return true;
    })
    .sort((a, b) => {
      const orderA =
        a.layout && typeof a.layout.order === 'number' ? a.layout.order : a.defaultOrder;
      const orderB =
        b.layout && typeof b.layout.order === 'number' ? b.layout.order : b.defaultOrder;
      return orderA - orderB;
    });

  if (filterSectionKey) {
    sections = sections.filter((section) => section.sectionKey === filterSectionKey);
  }

  return (
    <Box sx={{ bgcolor: 'white', color: 'black' }}>
      {sections.map((section) => {
        if (section.id === 'executive-dashboard') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintDashboard
                snapshot={snapshot}
                docsFiles={docsFiles}
                onReadyForPrint={onReadyForPrint}
                printRefreshToken={printRefreshToken}
                mapSnapshots={mapSnapshots}
              />
            </PrintPage>
          );
        }

        if (section.id === 'details-car') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintCarDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-sigef') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSigefDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-sncr') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSncrDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-cafir') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintCafirDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-ccir') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintCcirDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-cnd') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintCndDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-entidades') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintEntidadesDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-legal-civil') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintLegalCivilDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'details-legal-criminal') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintLegalCriminalDetails snapshot={snapshot} />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-biomas') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="biomas" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-ucs') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="uc" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-florestas-publicas') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="florestas" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-terras-indigenas') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="terras_indigenas" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-aldeias') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="aldeias" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-terras-quilombolas') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="quilombolas" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-assentamentos-incra') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="assentamentos" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-mineracao') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="mineracao" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-embargos-ibama') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="embargos_ibama" />
            </PrintPage>
          );
        }

        if (section.id === 'overlaps-pra') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintSobreposicaoDetails snapshot={snapshot} themeKey="pra" />
            </PrintPage>
          );
        }

        if (section.id === 'geoespacial') {
          return (
            <PrintPage
              key={section.id}
              title={section.title}
              snapshot={snapshot}
              userName={userName}
            >
              <ReportPrintGeoespacialDetails
                snapshot={snapshot}
                printRefreshToken={printRefreshToken}
                mapSnapshots={mapSnapshots}
              />
            </PrintPage>
          );
        }

        return (
          <PrintPage
            key={section.id}
            title={section.title}
            snapshot={snapshot}
            userName={userName}
          />
        );
      })}
    </Box>
  );
}
